import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BaseCard from './BaseCard';
import clsx from 'clsx';

// ***** TYPE DEFINITIONS SIMPLIFIED *****
interface SubItemData {
  label: string;
  onClick: () => void;
  path?: string;
}

// Data structure for the items array
interface NavItemData {
  icon: React.ReactElement<{ className?: string }>;
  label: string;
  path: string;
  subItems?: SubItemData[];
}

// Props needed by the actual NavigationItem *component* for rendering
interface NavigationItemComponentProps {
  icon: React.ReactElement<{ className?: string }>;
  label: string;
  isActive: boolean;
  onClick: () => void;
}
// ***** TYPE DEFINITIONS SIMPLIFIED END *****

interface SideNavProps {
  className?: string;
}

const NavigationItem = ({ icon, label, isActive, onClick }: NavigationItemComponentProps) => {
  const textColor = isActive ? 'text-primary-base' : 'text-gray-500';
  const bgColor = isActive ? 'bg-primary-50' : 'hover:bg-primary-50';
  const iconColor = isActive ? 'text-primary-base' : 'text-gray-500';

  return (
    <div
      onClick={onClick}
      className={`flex items-center px-4 py-2 cursor-pointer rounded-md ${bgColor} ${textColor}`}
    >
      <div className={`size-6 ${iconColor}`}>
        {React.cloneElement(icon, {
          className: `size-6 ${iconColor}`,
        })}
      </div>
      <span className={clsx("ml-3", isActive && "font-semibold")}>{label}</span>
    </div>
  );
};

// Define icons separately for clarity (optional, but helps avoid massive code block)
const HomeIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.47 3.841a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 0 1.06-1.061l-8.689-8.69a2.25 2.25 0 0 0-3.182 0l-8.69 8.69a.75.75 0 1 0 1.061 1.06l8.69-8.689Z" />
    <path d="m12 5.432 8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 0-.75-.75h-3a.75.75 0 0 0-.75.75V21a.75.75 0 0 1-.75.75H5.625a1.875 1.875 0 0 1-1.875-1.875v-6.198a2.29 2.29 0 0 0 .091-.086L12 5.432Z" />
  </svg>
);

const CommunitiesIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0ZM15.75 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM2.25 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM6.31 15.117A6.745 6.745 0 0 1 12 12a6.745 6.745 0 0 1 6.709 7.498.75.75 0 0 1-.372.568A12.696 12.696 0 0 1 12 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 0 1-.372-.568 6.787 6.787 0 0 1 1.019-4.38Z" clipRule="evenodd" />
    <path d="M5.082 14.254a8.287 8.287 0 0 0-1.308 5.135 9.687 9.687 0 0 1-1.764-.44l-.115-.04a.563.563 0 0 1-.373-.487l-.01-.121a3.75 3.75 0 0 1 3.57-4.047ZM20.226 19.389a8.287 8.287 0 0 0-1.308-5.135 3.75 3.75 0 0 1 3.57 4.047l-.01.121a.563.563 0 0 1-.373.486l-.115.04c-.567.2-1.156.349-1.764.441Z" />
  </svg>
);

export const SideNav: React.FC<SideNavProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // ***** DEFINE DATA INSIDE COMPONENT START *****
  // Define the base data structure for navigation items
  const navigationData: NavItemData[] = [
    { icon: HomeIcon, label: 'Home', path: '/' },
    {
      icon: CommunitiesIcon,
      label: 'Communities',
      path: '/communities',
      subItems: [
        { label: 'My Communities', onClick: () => navigate('/communities?my_communities=true'), path: '/communities?my_communities=true' },
      ],
    },
    // { icon: ProjectsIcon, label: 'Projects', path: '/projects' },
    // { icon: ResourcesIcon, label: 'Resources', path: '/resources' },
  ];
  // ***** DEFINE DATA INSIDE COMPONENT END *****

  // Helper to check if a path is active, including sub-paths
  const isPathActive = (basePath?: string) => {
    if (!basePath) return false;
    if (basePath === '/') {
      return location.pathname === '/';
    }
    return location.pathname === basePath || location.pathname.startsWith(basePath + '/');
  };

  return (
    <nav className={`relative flex flex-col w-[220px] ${className}`}>
      <div className="flex flex-col text-base max-md:mt-6">
        <BaseCard className="px-4 py-6 fill-white">
          <div className="flex flex-col">
            <div className="flex flex-col w-full">
              {/* Map over the base data */}
              {navigationData.map((item) => {
                const isActive = isPathActive(item.path);
                return (
                  <div key={item.path} className="mt-4 first:mt-0 whitespace-nowrap">
                    {/* Render the main navigation item using NavigationItem component */}
                    <NavigationItem
                      icon={item.icon}
                      label={item.label}
                      isActive={isActive}
                      onClick={() => navigate(item.path)} // Main item click always navigates
                    />
                    {/* Conditionally render sub-items (plain divs, not NavigationItem component) */}
                    {isActive && item.subItems && (
                      <div className="mt-2 pl-10 space-y-1">
                        {item.subItems.map((subItem) => {
                          const isSubItemActive = subItem.path ? location.pathname === subItem.path : false;
                          return (
                            <div
                              key={subItem.label}
                              onClick={subItem.onClick} // Use the onClick defined in subItem data
                              className={`flex items-center px-3 py-1.5 cursor-pointer rounded-md text-sm ${
                                isSubItemActive
                                  ? 'bg-primary-50 text-primary-base font-medium'
                                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                              }`}
                            >
                              {/* Sub-items don't use the NavigationItem component's icon slot */}
                              {/* Apply conditional bolding to sub-item label */}
                              <span className={clsx(isSubItemActive && "font-semibold")}>{subItem.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </BaseCard>
      </div>
    </nav>
  );
};